/*
 * @author bai <ghimping@alpstein.my>
 * @link https://www.alpstein.my
 * @copyright Copyright (c) Alpstein Software
 *            _____                    _____                    _____
 *           /\    \                  /\    \                  /\    \
 *          /::\    \                /::\    \                /::\    \
 *         /::::\    \              /::::\    \               \:::\    \
 *        /::::::\    \            /::::::\    \               \:::\    \
 *       /:::/\:::\    \          /:::/\:::\    \               \:::\    \
 *      /:::/__\:::\    \        /:::/__\:::\    \               \:::\    \
 *     /::::\   \:::\    \      /::::\   \:::\    \              /::::\    \
 *    /::::::\   \:::\    \    /::::::\   \:::\    \    ____    /::::::\    \
 *   /:::/\:::\   \:::\ ___\  /:::/\:::\   \:::\    \  /\   \  /:::/\:::\    \
 *  /:::/__\:::\   \:::|    |/:::/  \:::\   \:::\____\/::\   \/:::/  \:::\____\
 *  \:::\   \:::\  /:::|____|\::/    \:::\  /:::/    /\:::\  /:::/    \::/    /
 *   \:::\   \:::\/:::/    /  \/____/ \:::\/:::/    /  \:::\/:::/    / \/____/
 *    \:::\   \::::::/    /            \::::::/    /    \::::::/    /
 *     \:::\   \::::/    /              \::::/    /      \::::/____/
 *      \:::\  /:::/    /               /:::/    /        \:::\    \
 *       \:::\/:::/    /               /:::/    /          \:::\    \
 *        \::::::/    /               /:::/    /            \:::\    \
 *         \::::/    /               /:::/    /              \:::\____\
 *          \::/____/                \::/    /                \::/    /
 *           ~~                       \/____/                  \/____/
 *
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { injectIntl, FormattedMessage } from "react-intl";
import { Button, WhiteSpace } from "antd-mobile";
import { connect } from "react-redux";
import TextInput from "../../../components/TextInput";
import Theme from "../../../utils/Theme";
import Toast from "../../../utils/Toast";
import {
  identifyMobileNumError,
  identifyPasswordError,
  validateMobileNum,
  validatePassword
} from "../../../utils/FieldValidator";
import * as selectors from "../../../selectors";
import * as verificationAction from "../../../actions/verification";

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0.5em 1.4em;
  text-align: center;
  color: grey;
`;

const StyledButton = styled(Button)`
  && {
    cursor: pointer;
    font-size: 1em
    border-radius: 25px;
    color: white;
    background-color: ${Theme.PrimaryColor};
  }
`;

const StyledDescription = styled.span`
  line-height: 1.4em;
  font-size: 0.9em;
  color: gray;
`;

const StyledPasswordHint = styled.span`
  font-size: 0.75em;
  color: gray;
`;

class SignupSection extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mobile_number: {
        value: "",
        error: false,
        error_message: ""
      },
      password: {
        value: "",
        error: false,
        error_message: ""
      }
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { history, requestStatus } = this.props;

    if (requestStatus !== nextProps.requestStatus) {
      if (nextProps.requestStatus) {
        history.push("/verify");
      }
    }
  }

  validateFields = () => {
    const { mobile_number, password } = this.state;

    if (!validateMobileNum(mobile_number.value)) {
      mobile_number.error = true;
      mobile_number.error_message = identifyMobileNumError(mobile_number.value);
    } else {
      mobile_number.error = false;
      mobile_number.error_message = "";
    }

    if (!validatePassword(password.value)) {
      password.error = true;
      password.error_message = identifyPasswordError(password.value);
    } else {
      password.error = false;
      password.error_message = "";
    }

    this.setState({
      mobile_number: mobile_number,
      password: password
    });
  };

  onSubmit = () => {
    const { mobile_number, password } = this.state;

    this.validateFields();
    if (!mobile_number.error && !password.error) {
      this.requestPhoneVerificationCode();
    }
  };

  onMobileNumberChange = txt => {
    this.setState({
      mobile_number: {
        value: txt,
        error: false,
        error_message: ""
      }
    });
  };

  onPasswordChange = txt => {
    this.setState({
      password: {
        value: txt,
        error: false,
        error_message: ""
      }
    });
  };

  requestPhoneVerificationCode = () => {
    const {
      requestPhoneVerificationCodeRequest,
      setMobileNumberAndPassword
    } = this.props;
    const mobile_number = this.state.mobile_number.value.replace(/\s/g, "");
    const password = this.state.password.value;

    const data = {
      type: "phone",
      prefix: "+60",
      number: mobile_number
    };
    requestPhoneVerificationCodeRequest(data);
    setMobileNumberAndPassword(mobile_number, password);
  };

  render() {
    const { mobile_number, password } = this.state;
    const { intl, requestLoading } = this.props;

    return (
      <StyledContainer>
        <StyledDescription>
          <FormattedMessage
            id="auth.register.description"
            defaultMessage="Create your own account to top up your credits to the wallets & enjoy other benefits too!"
            description="Signup Description"
          />
        </StyledDescription>
        <WhiteSpace size={"lg"} />
        <TextInput
          type={"mobile"}
          error={mobile_number.error}
          disabled={requestLoading}
          onChange={txt => this.onMobileNumberChange(txt)}
          onErrorClick={() => {
            Toast.error(
              intl.formatMessage({
                id: mobile_number.error_message,
                defaultMessage: "Invalid Mobile Number Format"
              })
            );
          }}
        />
        <TextInput
          type={"password"}
          error={password.error}
          disabled={requestLoading}
          onChange={txt => this.onPasswordChange(txt)}
          onErrorClick={() => {
            Toast.error(
              intl.formatMessage({
                id: password.error_message,
                defaultMessage: "Invalid Password Format"
              })
            );
          }}
        />
        <WhiteSpace size={"sm"} />
        <StyledPasswordHint>
          <FormattedMessage
            id="auth.register.pass.hint"
            defaultMessage="It must have at least 8 characters, 1 capital letter, 1 small letter and a number, not contain 3 consecutive characters."
          />
        </StyledPasswordHint>
        <WhiteSpace size={"xl"} />
        <WhiteSpace size={"xl"} />
        <StyledButton
          disabled={requestLoading}
          loading={requestLoading}
          activeStyle={{ backgroundColor: Theme.ActivePrimary }}
          onClick={() => this.onSubmit()}
        >
          {requestLoading ? (
            <FormattedMessage
              id="app.loading"
              defaultMessage="Loading"
              description="Loading Title"
            />
          ) : (
            <FormattedMessage
              id="auth.register.title"
              defaultMessage="Register now"
              description="Register title button at auth page"
            />
          )}
        </StyledButton>
        <WhiteSpace size={"xl"} />
      </StyledContainer>
    );
  }
}

SignupSection.propTypes = {
  intl: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  requestStatus: PropTypes.bool,
  requestLoading: PropTypes.bool,
  requestPhoneVerificationCodeRequest: PropTypes.func.isRequired,
  setMobileNumberAndPassword: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  return {
    requestStatus: selectors.getPhoneVerificationRequestStatus(state),
    requestLoading: selectors.getRequestPhoneVerificationLoading(state)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    requestPhoneVerificationCodeRequest: data =>
      dispatch(verificationAction.requestPhoneVerificationCodeRequest(data)),
    setMobileNumberAndPassword: (phone, password) =>
      dispatch(verificationAction.setMobileNumberAndPassword(phone, password))
  };
}

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(SignupSection)
);



// WEBPACK FOOTER //
// ./src/containers/AuthPage/SignupSection/index.js